import type { LoginAuditRow } from './login-audit-types';
import {
  setAuditSortCol,
  setAuditSortAsc,
  setAuditPage,
  auditSortCol,
  auditSortAsc,
  auditSearch,
  auditPage,
  auditPageSize,
  auditUserType,
  fmtLoginDate,
  laRecalculate,
} from './login-audit-page';
import { escapeHtml } from '../utils/html';

// ── State toggle ──────────────────────────────────────────────────────────

export function showAuditState(
  s: 'empty' | 'loading' | 'error' | 'results',
  msg = '',
): void {
  document.getElementById('laEmpty')!   .classList.toggle('hidden', s !== 'empty');
  document.getElementById('laLoading')! .classList.toggle('hidden', s !== 'loading');
  document.getElementById('laErrBox')!  .classList.toggle('hidden', s !== 'error');
  document.getElementById('laResults')! .classList.toggle('hidden', s !== 'results');

  if (s === 'loading') {
    const lbl = document.getElementById('laLoadingLabel');
    if (lbl) lbl.textContent = msg || 'Loading…';
  }
  if (s === 'error') {
    document.getElementById('laErrBox')!.textContent = msg;
    document.getElementById('laEmpty')!.classList.remove('hidden');
  }
}

// ── Main render entry ─────────────────────────────────────────────────────

export function renderLoginAuditResults(
  rows: LoginAuditRow[],
  sortCol: string,
  sortAsc: boolean,
): void {
  renderAuditKPIs(rows);
  renderTop10Chart(rows);
  renderAuditTable(rows, sortCol, sortAsc);
}

// ── KPI cards ─────────────────────────────────────────────────────────────

function renderAuditKPIs(rows: LoginAuditRow[]): void {
  const grid = document.getElementById('laKpiGrid')!;
  if (!rows.length) { grid.innerHTML = ''; return; }

  const totalLogins   = rows.reduce((s, r) => s + r.loginsInRange, 0);
  const activeInRange = rows.filter((r) => r.loginsInRange > 0).length;
  const totalStaff    = rows.filter((r) => !r.isDriver).length;
  const totalDrivers  = rows.filter((r) =>  r.isDriver).length;

  const fromVal = (document.getElementById('laFrom') as HTMLInputElement).value;
  const toVal   = (document.getElementById('laTo')   as HTMLInputElement).value;
  let avgPerDay = '—';
  if (fromVal && toVal) {
    const dayCount = Math.max(
      1,
      Math.ceil(
        (new Date(toVal + 'T23:59:59').getTime() - new Date(fromVal + 'T00:00:00').getTime()) / 86_400_000,
      ),
    );
    avgPerDay = (totalLogins / dayCount).toFixed(1);
  }

  grid.innerHTML = `
    <div class="kpi-card kpi-hi">
      <div class="kpi-val">${totalLogins.toLocaleString()}</div>
      <div class="kpi-lbl">Logins in Range</div>
      <div class="kpi-sub">across all users</div>
    </div>
    <div class="kpi-card kpi-hi">
      <div class="kpi-val">${activeInRange}</div>
      <div class="kpi-lbl">Active Users</div>
      <div class="kpi-sub">logged in during range</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-val">${avgPerDay}</div>
      <div class="kpi-lbl">Avg Logins / Day</div>
      <div class="kpi-sub">in selected period</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-val">${rows.length}</div>
      <div class="kpi-lbl">Total Active Users</div>
      <div class="kpi-sub">${totalStaff} staff · ${totalDrivers} drivers</div>
    </div>
  `.trim();
}

// ── Top 10 most active bar chart ──────────────────────────────────────────

function renderTop10Chart(rows: LoginAuditRow[]): void {
  const wrap = document.getElementById('laTopChart')!;
  if (!rows.length) { wrap.innerHTML = ''; return; }

  const top10 = [...rows]
    .filter((r) => r.loginsInRange > 0)
    .sort((a, b) => b.loginsInRange - a.loginsInRange)
    .slice(0, 10);

  if (!top10.length) {
    wrap.innerHTML = '<div class="la-no-data">No logins recorded in the selected range.</div>';
    return;
  }

  const maxVal = top10[0].loginsInRange;

  const barsHtml = top10.map((r, i) => {
    const pct   = maxVal > 0 ? (r.loginsInRange / maxVal) * 100 : 0;
    const color = i === 0 ? 'var(--la-top1)'
                : i < 3  ? 'var(--la-top3)'
                : 'var(--la-bar)';
    const label = r.displayName !== r.userName
      ? `${escapeHtml(r.displayName)} <span class="la-email">${escapeHtml(r.userName)}</span>`
      : escapeHtml(r.userName);

    return `
      <div class="la-chart-row">
        <div class="la-rank">${i + 1}</div>
        <div class="la-chart-label">${label}</div>
        <div class="la-chart-bar-track">
          <div class="la-chart-bar-fill" style="width:${pct.toFixed(1)}%;background:${color}"></div>
        </div>
        <div class="la-chart-val" style="color:${color}">${r.loginsInRange.toLocaleString()}</div>
      </div>
    `.trim();
  }).join('');

  wrap.innerHTML = `
    <div class="la-chart-card">
      <div class="la-chart-title">Top 10 Most Active Users (by logins in range)</div>
      <div class="la-chart-body">${barsHtml}</div>
    </div>
  `.trim();
}

// ── Full detail table (with search + pagination) ──────────────────────────

export function renderAuditTable(
  rows: LoginAuditRow[],
  sortCol: string,
  sortAsc: boolean,
): void {
  type ColKey = keyof LoginAuditRow;

  const cols: { key: ColKey; label: string; fmt: (r: LoginAuditRow) => string }[] = [
    {
      key: 'displayName',
      label: 'User',
      fmt: (r) => r.displayName !== r.userName
        ? `<strong>${escapeHtml(r.displayName)}</strong><br><span class="la-email td-muted">${escapeHtml(r.userName)}</span>`
        : `<strong>${escapeHtml(r.userName)}</strong>`,
    },
    {
      key: 'loginsInRange',
      label: 'Logins in Range',
      fmt: (r) => `<span class="td-mono${r.loginsInRange > 0 ? ' la-login-count' : ''}">${r.loginsInRange.toLocaleString()}</span>`,
    },
    {
      key: 'lastLoginISO',
      label: 'Last Login',
      fmt: (r) => r.lastLoginISO
        ? `<span class="td-mono">${fmtLoginDate(r.lastLoginISO)}</span>`
        : `<span class="td-muted">—</span>`,
    },
  ];

  // 0 — User-type pre-filter (All / Staff / Drivers)
  const typeFiltered =
    auditUserType === 'staff'   ? rows.filter((r) => !r.isDriver) :
    auditUserType === 'drivers' ? rows.filter((r) =>  r.isDriver) :
    rows;

  // 1 — Sort
  const sorted = [...typeFiltered].sort((a, b) => {
    const av = a[sortCol as ColKey];
    const bv = b[sortCol as ColKey];
    const dir = sortAsc ? 1 : -1;
    if (av === null && bv === null) return 0;
    if (av === null) return dir;
    if (bv === null) return -dir;
    return typeof av === 'string'
      ? (av as string).localeCompare(bv as string) * dir
      : ((av as number) - (bv as number)) * dir;
  });

  // 2 — Search filter (name OR email, case-insensitive)
  const term = auditSearch.toLowerCase().trim();
  const filtered = term
    ? sorted.filter(
        (r) =>
          r.displayName.toLowerCase().includes(term) ||
          r.userName.toLowerCase().includes(term),
      )
    : sorted;

  // 3 — Pagination
  const pageSize    = auditPageSize > 0 ? auditPageSize : filtered.length;
  const totalPages  = Math.ceil(filtered.length / pageSize) || 1;
  const currentPage = Math.min(auditPage, totalPages - 1);
  const pageStart   = currentPage * pageSize;
  const pageRows    = filtered.slice(pageStart, pageStart + pageSize);

  // 4 — Column headers
  const thHtml = cols.map((c) => {
    const active = c.key === sortCol;
    const arrow  = active ? (sortAsc ? '▲' : '▼') : '▲';
    return `<th class="${active ? 'sort-active' : ''}" onclick="window.__laSortBy('${c.key}')">${c.label}<span class="sort-arrow ${active ? '' : 'sort-arrow-dim'}">${arrow}</span></th>`;
  }).join('');

  // 5 — Rows
  const rowsHtml = pageRows.map((r) => {
    const cls = r.loginsInRange > 0 ? 'la-row-active' : '';
    return `<tr class="${cls}">${cols.map((c) => `<td>${c.fmt(r)}</td>`).join('')}</tr>`;
  }).join('');

  // 6 — Write table
  const table = document.getElementById('laTable') as HTMLElement;
  table.innerHTML = `<thead><tr>${thHtml}</tr></thead><tbody>${rowsHtml}</tbody>`;

  // 7 — Count display
  const countEl = document.getElementById('laCount');
  if (countEl) {
    const typeTotal = typeFiltered.length;
    countEl.textContent = term
      ? `${filtered.length} of ${typeTotal} users`
      : `${typeTotal} users`;
  }

  // 8 — Pagination controls
  renderPagination(currentPage, totalPages, filtered.length, pageStart, pageSize);

  // 9 — Sort handler (resets to first page so sorted data starts at top)
  (window as Window & { __laSortBy?: (col: string) => void }).__laSortBy = (col: string) => {
    if (auditSortCol === col) setAuditSortAsc(!auditSortAsc);
    else { setAuditSortCol(col); setAuditSortAsc(false); }
    setAuditPage(0);
    laRecalculate();
  };
}

// ── Pagination controls ───────────────────────────────────────────────────

function renderPagination(
  currentPage: number,
  totalPages: number,
  totalRows: number,
  pageStart: number,
  pageSize: number,
): void {
  const el = document.getElementById('laPagination');
  if (!el) return;

  if (totalPages <= 1) { el.innerHTML = ''; return; }

  // Build page number set: always include first, last, and a window around current
  const visible = new Set<number>();
  visible.add(0);
  visible.add(totalPages - 1);
  for (let i = Math.max(0, currentPage - 2); i <= Math.min(totalPages - 1, currentPage + 2); i++) {
    visible.add(i);
  }

  const sortedPages = [...visible].sort((a, b) => a - b);
  const btns: string[] = [];
  let prev = -1;
  for (const p of sortedPages) {
    if (p > prev + 1) btns.push('<span class="la-pg-ellipsis">…</span>');
    btns.push(
      `<button class="la-pg-btn${p === currentPage ? ' la-pg-active' : ''}" data-page="${p}">${p + 1}</button>`,
    );
    prev = p;
  }

  const pageEnd = Math.min(pageStart + pageSize, totalRows);

  el.innerHTML = `
    <button class="la-pg-btn" data-page="${currentPage - 1}" ${currentPage === 0 ? 'disabled' : ''}>‹</button>
    ${btns.join('')}
    <button class="la-pg-btn" data-page="${currentPage + 1}" ${currentPage >= totalPages - 1 ? 'disabled' : ''}>›</button>
    <span class="la-pg-info">Showing ${pageStart + 1}–${pageEnd} of ${totalRows.toLocaleString()}</span>
  `.trim();
}
